
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";

type JournalEntry = Database["public"]["Tables"]["journal_entries"]["Row"];
type MoodRating = "1" | "2" | "3" | "4" | "5";

interface JournalSubmitData {
  content: string;
  moodRating: MoodRating;
  selectedTags: string[];
}

export function useJournal() {
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [streakCount, setStreakCount] = useState(0);
  const session = useSession();
  const { toast } = useToast();

  const fetchJournalEntries = async () => {
    if (!session?.user) return;

    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJournalEntries(data || []);

      // Calculate streak
      const today = new Date().toISOString().split('T')[0];
      let currentStreak = 0;
      let lastDate = today;

      data?.forEach((entry) => {
        const entryDate = new Date(entry.created_at).toISOString().split('T')[0];
        if (entryDate === lastDate) {
          currentStreak++;
          lastDate = new Date(new Date(entryDate).getTime() - 86400000)
            .toISOString()
            .split('T')[0];
        } else {
          return;
        }
      });

      setStreakCount(currentStreak);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    }
  };

  const handleJournalSubmit = async (data: JournalSubmitData) => {
    if (!session?.user) return;

    try {
      const { error: journalError } = await supabase
        .from('journal_entries')
        .insert({
          user_id: session.user.id,
          title: new Date().toLocaleDateString(),
          content: data.content,
          mood_rating: data.moodRating as MoodRating,
          mood_tags: data.selectedTags,
        });

      if (journalError) throw journalError;
      
      // Toast message removed

      fetchJournalEntries();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      toast({
        title: "Error",
        description: "Failed to save journal entry",
        variant: "destructive",
      });
    }
  };

  const handleSuggestVerse = async (data: JournalSubmitData) => {
    try {
      console.log('Suggesting verse for journal entry:', {
        contentLength: data.content.length,
        moodRating: data.moodRating,
        tagCount: data.selectedTags.length,
      });

      const { data: suggestion, error } = await supabase.functions.invoke('generate-journal-reflection', {
        body: {
          content: data.content,
          moodRating: data.moodRating,
          moodTags: data.selectedTags,
        },
      });

      if (error) {
        console.error('Error from edge function:', error);
        throw error;
      }

      console.log('Received verse suggestion:', suggestion);

      if (!suggestion || !suggestion.verse) {
        console.error('Invalid suggestion response:', suggestion);
        throw new Error('Received invalid suggestion response');
      }

      return {
        verse: suggestion.verse,
        reference: suggestion.reference,
        explanation: suggestion.explanation,
        prayer: suggestion.prayer,
      };
    } catch (error) {
      console.error('Error suggesting verse:', error);
      toast({
        title: "Error",
        description: "Failed to suggest verse. Please try again later.",
        variant: "destructive",
      });
      throw error;
    }
  };

  useEffect(() => {
    if (session?.user) {
      fetchJournalEntries();
    }
  }, [session]);

  return {
    journalEntries,
    streakCount,
    handleJournalSubmit,
    handleSuggestVerse,
  };
}
